import { safeLocalStorage } from "./safeLocalStorage";

export interface SupportedLanguage {
  code: string; // e.g. "en", "es", "fr", "de", "pt"
  name: string; // "English", "Spanish", "French", "German", "Portuguese"
  nativeName: string; // "English", "Español", "Français", "Deutsch", "Português"
  enabled: boolean;
  direction: "ltr" | "rtl";
  hreflang: string; // e.g. "en", "es", "fr", "de", "pt"
  flagCode?: string;
  indexingReady: boolean;
}

export type LanguagePreference = "auto" | string;

export const BASE_LANGUAGE_CODE = "en";
export const STORAGE_LANGUAGE_KEY = "biznearyou_language_preference";
export const STORAGE_SUGGESTION_DISMISSED_KEY = "biznearyou_lang_suggestion_dismissed";

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    enabled: true,
    direction: "ltr",
    hreflang: "en",
    indexingReady: true,
  },
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    enabled: true,
    direction: "ltr",
    hreflang: "es",
    indexingReady: true,
  },
  {
    code: "fr",
    name: "French",
    nativeName: "Français",
    enabled: true,
    direction: "ltr",
    hreflang: "fr",
    indexingReady: true,
  },
  {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    enabled: true,
    direction: "ltr",
    hreflang: "de",
    indexingReady: true,
  },
  {
    code: "pt",
    name: "Portuguese",
    nativeName: "Português",
    enabled: true,
    direction: "ltr",
    hreflang: "pt",
    indexingReady: true,
  },
];

export function getEnabledLanguages(): SupportedLanguage[] {
  return SUPPORTED_LANGUAGES.filter((l) => l.enabled);
}

export function getIndexingReadyLanguages(): SupportedLanguage[] {
  return SUPPORTED_LANGUAGES.filter((l) => l.enabled && l.indexingReady);
}

export function getLanguageByCode(code: string): SupportedLanguage {
  const clean = (code || "").toLowerCase().trim();
  const match = SUPPORTED_LANGUAGES.find((l) => l.code === clean && l.enabled);
  return match || SUPPORTED_LANGUAGES[0];
}

export function isValidLanguageCode(code: string): boolean {
  if (!code) return false;
  const clean = code.toLowerCase().trim();
  return SUPPORTED_LANGUAGES.some((l) => l.code === clean && l.enabled);
}

/**
 * Extract language prefix from route path
 * e.g. "/es/jobs/sydney-nsw-au" -> { langCode: "es", routeWithoutLang: "/jobs/sydney-nsw-au", hasLangPrefix: true }
 * e.g. "/jobs/sydney-nsw-au" -> { langCode: "en", routeWithoutLang: "/jobs/sydney-nsw-au", hasLangPrefix: false }
 */
export function parseRouteLanguage(path: string): {
  langCode: string;
  routeWithoutLang: string;
  hasLangPrefix: boolean;
} {
  if (!path || path === "/") {
    return { langCode: "en", routeWithoutLang: "/", hasLangPrefix: false };
  }

  const cleanPath = path.split("?")[0].split("#")[0];
  const parts = cleanPath.split("/").filter(Boolean);

  if (parts.length > 0) {
    const candidate = parts[0].toLowerCase();
    if (candidate !== "en" && isValidLanguageCode(candidate)) {
      const remaining = "/" + parts.slice(1).join("/");
      const queryAndHash = path.substring(cleanPath.length);
      const normalizedRemaining = remaining === "/" ? "/" : remaining;
      return {
        langCode: candidate,
        routeWithoutLang: normalizedRemaining + queryAndHash,
        hasLangPrefix: true,
      };
    }
  }

  return { langCode: "en", routeWithoutLang: path, hasLangPrefix: false };
}

/**
 * Build URL with target language prefix
 * e.g. buildLanguageUrl("/jobs/minnesota", "es") -> "/es/jobs/minnesota"
 * e.g. buildLanguageUrl("/es/jobs/minnesota", "en") -> "/jobs/minnesota"
 */
export function buildLanguageUrl(path: string, targetLangCode: string): string {
  const { routeWithoutLang } = parseRouteLanguage(path);
  const cleanTarget = (targetLangCode || "en").toLowerCase().trim();

  if (cleanTarget === "en" || !isValidLanguageCode(cleanTarget)) {
    return routeWithoutLang.startsWith("/") ? routeWithoutLang : "/" + routeWithoutLang;
  }

  const normalizedRoute = routeWithoutLang.startsWith("/") ? routeWithoutLang : "/" + routeWithoutLang;
  if (normalizedRoute === "/") {
    return `/${cleanTarget}`;
  }
  return `/${cleanTarget}${normalizedRoute}`;
}

/**
 * Detect user browser language
 */
export function detectBrowserLanguage(): string {
  if (typeof window === "undefined" || !navigator) return "en";
  const languages = navigator.languages || [navigator.language || "en"];
  for (const lang of languages) {
    const code = (lang || "").split("-")[0].toLowerCase();
    if (isValidLanguageCode(code)) {
      return code;
    }
  }
  return "en";
}

/**
 * Get stored language preference ("auto" or explicit language code)
 */
export function getLanguagePreference(): LanguagePreference {
  const saved = safeLocalStorage.getItem(STORAGE_LANGUAGE_KEY);
  if (!saved || saved === "auto") return "auto";
  if (isValidLanguageCode(saved)) return saved;
  return "auto";
}

/**
 * Save language preference
 */
export function saveLanguagePreference(preference: LanguagePreference): void {
  safeLocalStorage.setItem(STORAGE_LANGUAGE_KEY, preference);
}

/**
 * Resolve effective language
 * Priority:
 * 1. Current route URL locale (if URL explicitly has /es/ or /fr/)
 * 2. Explicit saved user preference
 * 3. Auto / browser preference
 * 4. Original English fallback
 */
export function resolveEffectiveLanguage(path: string, userPreference?: LanguagePreference): string {
  const { langCode, hasLangPrefix } = parseRouteLanguage(path);
  if (hasLangPrefix && isValidLanguageCode(langCode)) {
    return langCode;
  }

  const pref = userPreference || getLanguagePreference();
  if (pref !== "auto" && isValidLanguageCode(pref)) {
    return pref;
  }

  const browserLang = detectBrowserLanguage();
  if (isValidLanguageCode(browserLang)) {
    return browserLang;
  }

  return BASE_LANGUAGE_CODE;
}

/**
 * Apply HTML lang and direction attributes to document
 */
export function applyDocumentLanguage(langCode: string): void {
  if (typeof document === "undefined") return;
  const langObj = getLanguageByCode(langCode);
  document.documentElement.lang = langObj.code;
  document.documentElement.dir = langObj.direction;

  if (langObj.direction === "rtl") {
    document.body.classList.add("rtl");
    document.body.classList.remove("ltr");
  } else {
    document.body.classList.add("ltr");
    document.body.classList.remove("rtl");
  }
}

/**
 * Generates alternate hreflang tags for international SEO
 */
export function getHreflangTags(pathname: string): string[] {
  const { routeWithoutLang } = parseRouteLanguage(pathname || "/");
  const normalizedPath = routeWithoutLang.startsWith("/") ? routeWithoutLang : "/" + routeWithoutLang;
  const baseUrl = "https://getbiznearyou.com";
  
  const tags: string[] = [
    `<link rel="alternate" hreflang="en" href="${baseUrl}${normalizedPath}" />`,
    `<link rel="alternate" hreflang="x-default" href="${baseUrl}${normalizedPath}" />`
  ];

  SUPPORTED_LANGUAGES.forEach((lang) => {
    if (lang.code !== "en" && lang.indexingReady) {
      const langUrl = `${baseUrl}/${lang.code}${normalizedPath === "/" ? "" : normalizedPath}`;
      tags.push(`<link rel="alternate" hreflang="${lang.hreflang}" href="${langUrl}" />`);
    }
  });

  return tags;
}

/**
 * Get current locale prefix from pathname (e.g. "/es/jobs/minnesota" -> "es", "/jobs/minnesota" -> "en")
 */
export function getLocaleFromPathname(path: string): string {
  return parseRouteLanguage(path || "/").langCode;
}

/**
 * Strip locale prefix from route (e.g. "/es/jobs/minnesota" -> "/jobs/minnesota")
 */
export function stripLocalePrefix(path: string): string {
  return parseRouteLanguage(path || "/").routeWithoutLang;
}

/**
 * Build localized path for target language, preserving canonical English without prefix
 * e.g. buildLocalizedPath("es", "/jobs/minnesota") -> "/es/jobs/minnesota"
 * e.g. buildLocalizedPath("en", "/es/jobs/minnesota") -> "/jobs/minnesota"
 */
export function buildLocalizedPath(targetLang: string, path: string): string {
  return buildLanguageUrl(path, targetLang);
}

/**
 * Navigate to localized URL path for target language
 */
export function navigateToLanguage(
  targetLang: string,
  currentPath: string,
  onNavigate?: (path: string) => void
): string {
  const targetUrl = buildLanguageUrl(currentPath, targetLang);
  if (typeof window !== "undefined" && window.location.pathname !== targetUrl) {
    if (onNavigate) {
      onNavigate(targetUrl);
    } else {
      window.history.pushState({}, "", targetUrl);
      window.dispatchEvent(new Event("popstate"));
    }
  }
  return targetUrl;
}

